import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Grid,
  Divider,
  CircularProgress,
  IconButton,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  TablePagination,
  Tooltip,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import {
  TaskAlt as TaskIcon,
  Person as PersonIcon,
  CheckCircle as CompletedIcon,
  PendingActions as PendingIcon,
  PlayCircleFilled as StartedIcon,
  Assignment as AssignmentIcon,
  QrCode as QrCodeIcon,
  PhoneAndroid as PhoneAndroidIcon
} from '@mui/icons-material';
import TimeService from '../../../services/TimeService';
import DeviceInfoModal from './DeviceInfoModal';
import { taskDataService, slotsToWeeklyDays } from '../../../services/taskDataService';
import type { TaskDto, SlotDto, CompanyCompletionDto } from '../../../services/taskDataService';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

// Görev zamanı chip bileşeni (Tasks.tsx'den alındı)
const TaskTimeChip = ({ status, label, size }: { status: string, label: string, size: "small" | "medium" }) => (
  <Chip
    label={label}
    size={size}
    sx={{ 
      backgroundColor: `${status}20`,
      color: status,
      fontSize: '0.7rem',
      height: 20
    }}
  />
);

// Haftanın günlerini temsil eden nesneler
const weekDays = [
  { value: 0, label: 'Pazar', shortLabel: 'Pz' },
  { value: 1, label: 'Pazartesi', shortLabel: 'Pt' },
  { value: 2, label: 'Salı', shortLabel: 'Sa' },
  { value: 3, label: 'Çarşamba', shortLabel: 'Ça' },
  { value: 4, label: 'Perşembe', shortLabel: 'Pe' },
  { value: 5, label: 'Cuma', shortLabel: 'Cu' },
  { value: 6, label: 'Cumartesi', shortLabel: 'Ct' }
];

// Gün adını al
const getDayName = (dayNumber: number) => {
  const day = weekDays.find(d => d.value === dayNumber);
  return day ? day.label : 'Bilinmeyen Gün';
};

// Günleri bul
const getTaskDays = (task: any) => {
  if (!task || !task.days) return [];
  
  return Object.keys(task.days).map(day => ({
    day: parseInt(day),
    dayName: getDayName(parseInt(day)),
    repetitionTimes: task.days[day].repetitionTimes || [],
    dailyRepetitions: task.days[day].dailyRepetitions || 1
  }));
};

interface WeeklyTasksTableProps {
  tasks: any[];
  statusFilter: string;
  onShowTaskDetail: (task: any) => void;
  onOpenQrPrintModal: (task: any, event: React.MouseEvent) => void;
  getStatusColor: (status: string) => string;
  getStatusIcon: (status: string) => React.ReactNode;
  getStatusLabel: (status: string) => string;
  getTaskTimeColor: (task: any, timeString: string) => string;
  t: TFunction;
  getTaskDays: (task: any) => any[];
}

// Haftalık görevler için tablo görünümü
const WeeklyTasksTable: React.FC<WeeklyTasksTableProps> = ({
  tasks,
  statusFilter,
  onShowTaskDetail,
  onOpenQrPrintModal,
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  getTaskTimeColor,
  t,
  getTaskDays
}) => {
  // Sayfalama için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Sayfalandırılmış task verileri
  const paginatedTasks = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return tasks.slice(startIndex, startIndex + rowsPerPage);
  }, [tasks, page, rowsPerPage]);
  
  // Filtre değişikliklerinde sayfa numarasını sıfırla
  useEffect(() => {
    setPage(0);
  }, [tasks]);
  
  // Sayfa değişimi
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişimi
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <TableContainer sx={{ 
      mt: 2,
      borderRadius: 1,
      overflow: 'hidden'
    }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell width="5%">{t('tasks.weeklyTasks.table.status')}</TableCell>
            <TableCell width="15%">{t('tasks.weeklyTasks.table.taskName')}</TableCell>
            <TableCell width="20%">{t('tasks.weeklyTasks.table.taskDays')}</TableCell>
            <TableCell width="20%">{t('tasks.weeklyTasks.table.taskTimes')}</TableCell>
            <TableCell width="10%">{t('tasks.weeklyTasks.table.tolerance')}</TableCell>
            <TableCell width="10%">{t('tasks.weeklyTasks.table.personnel')}</TableCell>
            <TableCell width="10%">{t('tasks.weeklyTasks.table.status')}</TableCell>
            <TableCell width="10%" align="right">{t('tasks.weeklyTasks.table.actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedTasks.map((task) => (
            <TableRow 
              key={task.id} 
              hover 
              onClick={() => onShowTaskDetail(task)} 
              sx={{
                cursor: statusFilter === 'completed' || statusFilter === 'missed' ? 'default' : 'pointer'
              }}
            >
              <TableCell>
                <Avatar
                  sx={{
                    width: 32, 
                    height: 32, 
                    bgcolor: `${getStatusColor(task.status)}20`,
                    color: getStatusColor(task.status)
                  }}
                >
                  {getStatusIcon(task.status)}
                </Avatar>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {task.name}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {getTaskDays(task).map((day) => (
                    <Chip
                      key={day.day}
                      label={day.dayName}
                      size="small"
                      sx={{ 
                        height: 20, 
                        fontSize: '0.7rem', 
                        bgcolor: 'info.50', 
                        color: 'info.main'
                      }}
                    />
                  ))}
                </Box>
              </TableCell>
              <TableCell>
                {getTaskDays(task).length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {getTaskDays(task).map((day) => (
                      <Box key={day.day} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', minWidth: 24 }}>
                          {day.dayName.substring(0, 2)}:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {day.repetitionTimes.map((time: string, index: number) => (
                            <TaskTimeChip
                              key={index}
                              label={time}
                              size="small"
                              status={getTaskTimeColor(task, time)}
                            />
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">-</Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip 
                  label={`${task.startTolerance || 15} ${t('tasks.minutes')}`}
                  size="small"
                  sx={{ 
                    height: 20, 
                    fontSize: '0.7rem', 
                    bgcolor: 'primary.50', 
                    color: 'primary.main'
                  }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" color={task.personnelName ? 'text.secondary' : 'error'} sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  fontSize: '0.8rem'
                }}>
                  <PersonIcon fontSize="small" sx={{ mr: 0.5, fontSize: 16 }} />
                  {task.personnelName || t('tasks.weeklyTasks.unassigned')}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={getStatusLabel(task.status)}
                  size="small"
                  sx={{
                    bgcolor: `${getStatusColor(task.status)}20`,
                    color: getStatusColor(task.status),
                    fontWeight: 'medium',
                    height: 20,
                    fontSize: 11,
                  }}
                />
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {task.completionType === 'qr' && !task.fromDatabase && statusFilter !== 'completed' && statusFilter !== 'missed' && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenQrPrintModal(task, e);
                      }}
                      sx={{ color: 'primary.main', p: 0.5 }}
                    >
                      <QrCodeIcon fontSize="small" />
                    </IconButton>
                  )}
                  {statusFilter !== 'completed' && statusFilter !== 'missed' && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onShowTaskDetail(task);
                      }}
                      sx={{ color: 'info.main', p: 0.5, ml: 0.5 }}
                    >
                      <AssignmentIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
          {tasks.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                <Typography color="text.secondary">
                  {t('tasks.weeklyTasks.noTaskFound')}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      
      {/* Sayfalama */}
      {tasks.length > 0 && (
        <TablePagination
          component="div"
          count={tasks.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage={t('tasks.weeklyTasks.pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('tasks.weeklyTasks.pagination.displayedRows', { from, to, count })}
        />
      )}
    </TableContainer>
  );
};

// Tamamlanan haftalık görevler için tablo bileşeni
const CompletedWeeklyTasksTable: React.FC<{
  tasks: any[];
  getStatusColor: (status: string) => string;
  getStatusIcon: (status: string) => React.ReactNode;
  getStatusLabel: (status: string) => string;
  tableTitle?: string;
  t: TFunction;
  i18n: any;
  getWeekdayShort: (dateStr: string) => string;
}> = ({
  tasks,
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  tableTitle,
  t,
  i18n,
  getWeekdayShort
}) => {
  const [selectedDeviceInfo, setSelectedDeviceInfo] = useState<any>(null);
  const [deviceModalOpen, setDeviceModalOpen] = useState(false);

  // Cihaz bilgisi modalını açma işlevi
  const handleOpenDeviceModal = (deviceInfo: any) => {
    setSelectedDeviceInfo(deviceInfo);
    setDeviceModalOpen(true);
  };

  // Cihaz bilgisi modalını kapatma işlevi
  const handleCloseDeviceModal = () => {
    setDeviceModalOpen(false);
  };

  // Tarihi formatlama fonksiyonu
  const formatTimestamp = (timestamp: number) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString(i18n.language === 'en' ? 'en-US' : 'tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const currentTableTitle = tableTitle || t('tasks.weeklyTasks.completedTable.title');

  return (
    <>
      <TableContainer component={Paper} sx={{ 
        mt: 2, 
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
        borderRadius: 2,
        maxHeight: 'calc(100vh - 240px)',
        overflowY: 'auto'
      }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width="5%">{t('tasks.weeklyTasks.table.status')}</TableCell>
              <TableCell width="13%">{t('tasks.weeklyTasks.table.taskName')}</TableCell>
              <TableCell width="9%">{t('tasks.weeklyTasks.completedTable.taskDay')}</TableCell>
              <TableCell width="9%">{t('tasks.weeklyTasks.completedTable.taskTime')}</TableCell>
              <TableCell width="9%">{t('tasks.weeklyTasks.table.tolerance')}</TableCell>
              <TableCell width="13%">{t('tasks.weeklyTasks.table.personnel')}</TableCell>
              {currentTableTitle === t('tasks.weeklyTasks.completedTable.title') ? (
                <>
                  <TableCell width="13%">{t('tasks.weeklyTasks.completedTable.startTime')}</TableCell>
                  <TableCell width="13%">{t('tasks.weeklyTasks.completedTable.endTime')}</TableCell>
                  <TableCell width="10%">{t('tasks.weeklyTasks.completedTable.device')}</TableCell>
                </>
              ) : (
                <TableCell width="35%">{t('tasks.weeklyTasks.completedTable.missedTime')}</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((task) => (
              <TableRow 
                key={task.id} 
                hover
                sx={{ cursor: 'default' }}
              >
                <TableCell>
                  <Avatar
                    sx={{
                      width: 32, 
                      height: 32, 
                      bgcolor: `${getStatusColor(task.status)}20`,
                      color: getStatusColor(task.status)
                    }}
                  >
                    {getStatusIcon(task.status)}
                  </Avatar>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {task.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {task.taskDate || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {task.taskDate && (
                      <Chip
                        label={getWeekdayShort(task.taskDate)}
                        size="small"
                        sx={{ 
                          height: 20, 
                          fontSize: '0.7rem', 
                          bgcolor: 'info.50', 
                          color: 'info.main',
                          mr: 0.5
                        }}
                      />
                    )}
                    <TaskTimeChip
                      label={task.taskTime || '-'}
                      size="small"
                      status={task.status === 'completed' ? "#4CAF50" : "#F44336"} // Tamamlandıysa yeşil, kaçırıldıysa kırmızı
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={`${task.startTolerance || 15} ${t('tasks.minutes')}`}
                    size="small"
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem', 
                      bgcolor: 'primary.50', 
                      color: 'primary.main'
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color={task.personnelName ? 'text.secondary' : 'error'} sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    fontSize: '0.8rem'
                  }}>
                    <PersonIcon fontSize="small" sx={{ mr: 0.5, fontSize: 16 }} />
                    {task.personnelName || t('tasks.weeklyTasks.unassigned')}
                  </Typography>
                </TableCell>
                {currentTableTitle === t('tasks.weeklyTasks.completedTable.title') ? (
                  <>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                        {formatTimestamp(task.startedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="success.main" fontSize="0.8rem" fontWeight="medium">
                        {formatTimestamp(task.completedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {task.deviceInfo ? (
                        <Tooltip title={t('tasks.weeklyTasks.completedTable.deviceInfoTooltip')}>
                          <Button
                            size="small"
                            startIcon={<PhoneAndroidIcon />}
                            onClick={() => handleOpenDeviceModal(task.deviceInfo)}
                            sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                          >
                            {task.deviceInfo.model || task.deviceInfo.deviceType}
                          </Button>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  </>
                ) : (
                  <TableCell>
                    <Typography variant="body2" color="error.main" fontSize="0.8rem" fontWeight="medium">
                      {formatTimestamp(task.missedAt)}
                    </Typography>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {tasks.length === 0 && (
              <TableRow>
                <TableCell colSpan={currentTableTitle === t('tasks.weeklyTasks.completedTable.title') ? 9 : 7} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">
                    {currentTableTitle === t('tasks.weeklyTasks.completedTable.title')
                      ? t('tasks.weeklyTasks.completedTable.noCompletedTasks') 
                      : t('tasks.weeklyTasks.completedTable.noMissedTasks')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Cihaz Bilgileri Modalı */}
      <DeviceInfoModal
        open={deviceModalOpen}
        onClose={handleCloseDeviceModal}
        deviceInfo={selectedDeviceInfo}
      />
    </>
  );
};

interface WeeklyTasksProps {
  companyId: string | null;
  statusFilter: string;
  personnelFilter: string;
  taskSearchTerm: string;
  viewMode: 'card' | 'list';
  personnel: any[];
  onShowTaskDetail: (task: any) => void;
  onOpenQrPrintModal: (task: any, event: React.MouseEvent) => void;
  getStatusColor: (status: string) => string;
  getStatusIcon: (status: string) => React.ReactNode;
  getStatusLabel: (status: string) => string;
  getTaskTimeColor: (task: any, timeString: string) => string;
  onDeleteTask?: (taskId: string, personnelId: string) => Promise<void>;
  onWeeklyTasksDataChange?: (tasks: any[]) => void;
  branchId?: string | null;
  isManager?: boolean;
  reloadKey?: number;
}

const WeeklyTasks: React.FC<WeeklyTasksProps> = ({
  companyId,
  statusFilter,
  personnelFilter,
  taskSearchTerm,
  viewMode,
  personnel,
  onShowTaskDetail,
  onOpenQrPrintModal,
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  getTaskTimeColor,
  onDeleteTask,
  onWeeklyTasksDataChange,
  branchId,
  isManager = false,
  reloadKey = 0
}) => {
  const { t, i18n } = useTranslation('common2');
  const [loading, setLoading] = useState(true);
  const [weeklyTasks, setWeeklyTasks] = useState<any[]>([]);
  const [completedWeeklyTasks, setCompletedWeeklyTasks] = useState<any[]>([]);
  const [missedWeeklyTasks, setMissedWeeklyTasks] = useState<any[]>([]);

  const weekDays = useMemo(() => [
    { value: 0, label: t('tasks.weeklyTasks.weekDays.sunday'), shortLabel: t('tasks.weeklyTasks.weekDaysShort.sunday') },
    { value: 1, label: t('tasks.weeklyTasks.weekDays.monday'), shortLabel: t('tasks.weeklyTasks.weekDaysShort.monday') },
    { value: 2, label: t('tasks.weeklyTasks.weekDays.tuesday'), shortLabel: t('tasks.weeklyTasks.weekDaysShort.tuesday') },
    { value: 3, label: t('tasks.weeklyTasks.weekDays.wednesday'), shortLabel: t('tasks.weeklyTasks.weekDaysShort.wednesday') },
    { value: 4, label: t('tasks.weeklyTasks.weekDays.thursday'), shortLabel: t('tasks.weeklyTasks.weekDaysShort.thursday') },
    { value: 5, label: t('tasks.weeklyTasks.weekDays.friday'), shortLabel: t('tasks.weeklyTasks.weekDaysShort.friday') },
    { value: 6, label: t('tasks.weeklyTasks.weekDays.saturday'), shortLabel: t('tasks.weeklyTasks.weekDaysShort.saturday') }
  ], [t]);
  
  const getDayName = useCallback((dayNumber: number) => {
    const day = weekDays.find(d => d.value === dayNumber);
    return day ? day.label : t('tasks.weeklyTasks.unknownDay');
  }, [weekDays, t]);

  const getTaskDays = useCallback((task: any) => {
    if (!task || !task.days) return [];
    
    return Object.keys(task.days).map(day => ({
      day: parseInt(day),
      dayName: getDayName(parseInt(day)),
      repetitionTimes: task.days[day].repetitionTimes || [],
      dailyRepetitions: task.days[day].dailyRepetitions || 1
    }));
  }, [getDayName]);
  
  const getWeekdayShort = useCallback((dateStr: string) => {
    try {
      if (!dateStr || dateStr === '-') return '';
      
      const dateParts = dateStr.split('-');
      if (dateParts.length !== 3) return '';
      
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1;
      const day = parseInt(dateParts[2], 10);
      
      const date = new Date(year, month, day);
      const weekDay = date.getDay();
      
      return weekDays.find(d => d.value === weekDay)?.shortLabel || '';
    } catch (error) {
      console.error("Tarih formatı hatası:", error);
      return '';
    }
  }, [weekDays]);

  // Haftalık görevleri SQL'den çek (Faz 5 DILIM 4) — görev + slot + tamamlama.
  useEffect(() => {
    if (!companyId) return;

    let cancelled = false;
    setLoading(true);

    const run = async () => {
      try {
        // Personel ad çözücü (prop SQL'den geliyor: id=firebaseUid)
        const personnelMap: Record<string, any> = {};
        (personnel || []).forEach((p: any) => { if (p?.id) personnelMap[p.id] = p; });
        const nameFromCompletion = (c: CompanyCompletionDto): string => {
          if (c.personnelFirstName) return `${c.personnelFirstName} ${c.personnelLastName || ''}`.trim();
          return personnelMap[c.personnelFirebaseUid || '']?.name || t('tasks.weeklyTasks.unassigned');
        };
        const toEpoch = (iso?: string | null): number | undefined =>
          iso ? new Date(iso).getTime() : undefined;
        const deviceFromCompletion = (c: CompanyCompletionDto): any =>
          (c.devicePlatform || c.deviceUserAgent)
            ? { model: c.devicePlatform, deviceType: c.devicePlatform, platform: c.devicePlatform, userAgent: c.deviceUserAgent }
            : null;

        // Sayfalı çekim yardımcıları
        const fetchTasks = async (): Promise<TaskDto[]> => {
          const acc: TaskDto[] = [];
          let page = 1;
          for (;;) {
            const res = await taskDataService.listTasks({ taskType: 'weekly', page, pageSize: 200 });
            acc.push(...res.items);
            if (res.items.length === 0 || page >= (res.totalPages || 1)) break;
            page++;
          }
          return acc;
        };
        const fetchCompletions = async (status: 'completed' | 'missed'): Promise<CompanyCompletionDto[]> => {
          const acc: CompanyCompletionDto[] = [];
          let page = 1;
          for (;;) {
            const res = await taskDataService.listCompletions({ taskType: 'weekly', status, page, pageSize: 1000 });
            acc.push(...res.items);
            if (res.items.length === 0 || page >= (res.totalPages || 1)) break;
            page++;
          }
          return acc;
        };

        const [tasksData, slotsData, completedData, missedData] = await Promise.all([
          fetchTasks(),
          taskDataService.listSlots('weekly'),
          fetchCompletions('completed'),
          fetchCompletions('missed'),
        ]);
        if (cancelled) return;

        // Slotları görev bazında grupla → RTDB-uyumlu `days` haritası
        const slotsByTask: Record<number, SlotDto[]> = {};
        (slotsData || []).forEach((s) => {
          (slotsByTask[s.taskId] = slotsByTask[s.taskId] || []).push(s);
        });

        const tasksList = (tasksData || []).map((tk) => ({
          id: tk.id,
          name: tk.name || t('tasks.weeklyTasks.unnamedTask'),
          description: tk.description || '',
          status: tk.status || 'pending',
          createdAt: tk.createdAt ? new Date(tk.createdAt).getTime() : Date.now(),
          personnelId: tk.personnelFirebaseUid || '',
          personnelName: personnelMap[tk.personnelFirebaseUid || '']?.name || t('tasks.weeklyTasks.unassigned'),
          completionType: tk.completionType || 'button',
          startTolerance: tk.startTolerance || 15,
          days: slotsToWeeklyDays(slotsByTask[tk.id] || []),
          groupId: tk.groupId ?? undefined,
        }));
        setWeeklyTasks(tasksList.sort((a, b) => b.createdAt - a.createdAt));

        const completedTasksList = (completedData || []).map((c) => ({
          id: `${c.taskId}_${c.taskDate}_${c.taskTime}`,
          taskId: c.taskId,
          name: c.taskName || t('tasks.weeklyTasks.unnamedTask'),
          description: c.taskDescription || '',
          status: 'completed',
          taskDate: c.taskDate,
          taskTime: c.taskTime,
          startedAt: toEpoch(c.startedAt),
          completedAt: toEpoch(c.completedAt),
          personnelId: c.personnelFirebaseUid || '',
          personnelName: nameFromCompletion(c),
          startTolerance: c.startTolerance || 15,
          deviceInfo: deviceFromCompletion(c),
          fromDatabase: true,
        }));
        setCompletedWeeklyTasks(completedTasksList.sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0)));

        const missedTasksList = (missedData || []).map((c) => ({
          id: `${c.taskId}_${c.taskDate}_${c.taskTime}`,
          taskId: c.taskId,
          name: c.taskName || t('tasks.weeklyTasks.unnamedTask'),
          description: c.taskDescription || '',
          status: 'missed',
          taskDate: c.taskDate,
          taskTime: c.taskTime,
          missedAt: toEpoch(c.missedAt),
          personnelId: c.personnelFirebaseUid || '',
          personnelName: nameFromCompletion(c),
          startTolerance: c.startTolerance || 15,
          fromDatabase: true,
        }));
        setMissedWeeklyTasks(missedTasksList.sort((a, b) => (b.missedAt || 0) - (a.missedAt || 0)));
      } catch (error) {
        if (!cancelled) console.error("Haftalık görevleri yüklerken hata:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [companyId, t, personnel, reloadKey]);

  // Görevleri filtrele
  const filteredTasks = useMemo(() => {
    let result = [...weeklyTasks];
    
    // Şube yöneticisi ve şube ID'si varsa, şubeye göre filtrele
    if (isManager && branchId) {
      result = result.filter(task => {
        // Görevin personeline göre şube kontrolü
        if (task.personnelId) {
          const person = personnel.find(p => p.id === task.personnelId);
          if (person) {
            // branchesId bir nesne olabilir veya string olabilir
            if (typeof person.branchesId === 'object' && person.branchesId !== null) {
              // Nesne durumunda, anahtarlarını kontrol et
              return Object.keys(person.branchesId).includes(branchId);
            } else {
              // String durumunda direk karşılaştır
              return person.branchesId === branchId;
            }
          }
        }
        return false;
      });
    }
    
    // Durum filtresi uygulanıyor
    if (statusFilter !== 'all') {
      if (statusFilter === 'completed') {
        // Durum 'completed' olduğunda, tamamlanan haftalık görevler completedWeeklyTasks'dan gelecek
        // Bu durumda boş dizi döndürüyoruz, çünkü completedWeeklyTasks ayrı bir tablo olarak gösterilecek
        return [];
      } else if (statusFilter === 'missed') {
        // Ana görev listesindeki kaçırılan görevler + veritabanındaki kaçırılan görevler
        result = [
          ...result.filter(task => task.status === 'missed'),
          ...missedWeeklyTasks
        ];
      } else {
        // Diğer durumlar için normal filtreleme
        result = result.filter(task => task.status === statusFilter);
      }
    }
    
    // Personel filtresi uygulanıyor
    if (personnelFilter !== 'all') {
      result = result.filter(task => task.personnelId === personnelFilter);
    }

    // Görev arama filtresi uygulanıyor
    if (taskSearchTerm !== '') {
      result = result.filter(task => 
        task.name.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
        task.description.toLowerCase().includes(taskSearchTerm.toLowerCase())
      );
    }
    
    return result;
  }, [weeklyTasks, missedWeeklyTasks, statusFilter, personnelFilter, taskSearchTerm, isManager, branchId, personnel]);

  // Tamamlanan görevleri filtrele
  const filteredCompletedTasks = useMemo(() => {
    let result = [...completedWeeklyTasks];
    
    // Şube yöneticisi ve şube ID'si varsa, şubeye göre filtrele
    if (isManager && branchId) {
      result = result.filter(task => {
        // Görevin personeline göre şube kontrolü
        if (task.personnelId) {
          const person = personnel.find(p => p.id === task.personnelId);
          if (person) {
            // branchesId bir nesne olabilir veya string olabilir
            if (typeof person.branchesId === 'object' && person.branchesId !== null) {
              // Nesne durumunda, anahtarlarını kontrol et
              return Object.keys(person.branchesId).includes(branchId);
            } else {
              // String durumunda direk karşılaştır
              return person.branchesId === branchId;
            }
          }
        }
        return false;
      });
    }
    
    // Personel filtresi uygulanıyor
    if (personnelFilter !== 'all') {
      result = result.filter(task => task.personnelId === personnelFilter);
    }

    // Görev arama filtresi uygulanıyor
    if (taskSearchTerm !== '') {
      result = result.filter(task => 
        task.name.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
        task.description.toLowerCase().includes(taskSearchTerm.toLowerCase())
      );
    }
    
    return result;
  }, [completedWeeklyTasks, personnelFilter, taskSearchTerm, isManager, branchId, personnel]);

  // Filtrelenmiş kaçırılan görevler
  const filteredMissedTasks = useMemo(() => {
    let result = [...missedWeeklyTasks];
    
    // Şube yöneticisi ve şube ID'si varsa, şubeye göre filtrele
    if (isManager && branchId) {
      result = result.filter(task => {
        // Görevin personeline göre şube kontrolü
        if (task.personnelId) {
          const person = personnel.find(p => p.id === task.personnelId);
          if (person) {
            // branchesId bir nesne olabilir veya string olabilir
            if (typeof person.branchesId === 'object' && person.branchesId !== null) {
              // Nesne durumunda, anahtarlarını kontrol et
              return Object.keys(person.branchesId).includes(branchId);
            } else {
              // String durumunda direk karşılaştır
              return person.branchesId === branchId;
            }
          }
        }
        return false;
      });
    }
    
    // Personel filtresi uygulanıyor
    if (personnelFilter !== 'all') {
      result = result.filter(task => task.personnelId === personnelFilter);
    }

    // Görev arama filtresi uygulanıyor
    if (taskSearchTerm !== '') {
      result = result.filter(task => 
        task.name.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
        task.description.toLowerCase().includes(taskSearchTerm.toLowerCase())
      );
    }
    
    return result;
  }, [missedWeeklyTasks, personnelFilter, taskSearchTerm, isManager, branchId, personnel]);

  // Filtrelenmiş verileri üst bileşene gönder
  useEffect(() => {
    if (onWeeklyTasksDataChange) {
      const tasksToExport = statusFilter === 'completed' ? filteredCompletedTasks : 
                            statusFilter === 'missed' ? filteredMissedTasks : 
                            filteredTasks;
      onWeeklyTasksDataChange(tasksToExport);
    }
  }, [filteredTasks, filteredCompletedTasks, filteredMissedTasks, statusFilter, onWeeklyTasksDataChange]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (weeklyTasks.length === 0 && statusFilter !== 'completed' && statusFilter !== 'missed') {
    return (
      <Box sx={{ textAlign: 'center', my: 8 }}>
        <AssignmentIcon sx={{ fontSize: 60, color: 'primary.light', mb: 2 }} />
        <Typography variant="h6" color="textSecondary">
          {
            statusFilter === 'all' && personnelFilter === 'all'
              ? t('tasks.weeklyTasks.noTasks.noneAdded')
              : t('tasks.weeklyTasks.noTasks.noMatchFilters')
          }
        </Typography>
      </Box>
    );
  }

  if (statusFilter === 'completed' && filteredCompletedTasks.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', my: 8 }}>
        <AssignmentIcon sx={{ fontSize: 60, color: 'primary.light', mb: 2 }} />
        <Typography variant="h6" color="textSecondary">
          {t('tasks.weeklyTasks.noTasks.noCompleted')}
        </Typography>
      </Box>
    );
  }

  if (statusFilter === 'missed' && filteredMissedTasks.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', my: 8 }}>
        <AssignmentIcon sx={{ fontSize: 60, color: 'primary.light', mb: 2 }} />
        <Typography variant="h6" color="textSecondary">
          {t('tasks.weeklyTasks.noTasks.noMissed')}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {viewMode === 'card' ? (
        <Grid container spacing={3}>
          {filteredTasks.map((task) => (
            <Grid item xs={12} sm={6} md={4} key={task.id}>
              <Card 
                onClick={() => onShowTaskDetail(task)}
                sx={{
                  height: '100%',
                  borderRadius: 12,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  cursor: statusFilter === 'completed' || statusFilter === 'missed' ? 'default' : 'pointer',
                  '&:hover': {
                    boxShadow: statusFilter === 'completed' || statusFilter === 'missed' 
                      ? '0 4px 12px rgba(0,0,0,0.05)' 
                      : '0 6px 16px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      sx={{
                        bgcolor: `${getStatusColor(task.status)}20`,
                        color: getStatusColor(task.status),
                        mr: 1,
                      }}
                    >
                      {getStatusIcon(task.status)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" noWrap>
                        {task.name}
                      </Typography>
                      <Chip
                        label={getStatusLabel(task.status)}
                        size="small"
                        sx={{
                          bgcolor: `${getStatusColor(task.status)}20`,
                          color: getStatusColor(task.status),
                          fontWeight: 'medium',
                          height: 20,
                          fontSize: 11,
                        }}
                      />
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 1.5 }} />

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 1.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      flexGrow: 0,
                    }}
                  >
                    {task.description || t('tasks.weeklyTasks.card.noDescription')}
                  </Typography>

                  <Box sx={{ mb: 2, flexGrow: 1 }}>
                    <Typography variant="body2" fontWeight="medium" color="primary" gutterBottom>
                      {t('tasks.weeklyTasks.card.weekDays')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {getTaskDays(task).map((day) => (
                        <Chip
                          key={day.day}
                          label={day.dayName}
                          size="small"
                          sx={{ 
                            height: 24, 
                            fontSize: '0.75rem', 
                            bgcolor: 'info.50', 
                            color: 'info.main'
                          }}
                        />
                      ))}
                    </Box>
                  </Box>

                  {getTaskDays(task).length > 0 && (
                    <Box sx={{ mb: 1.5 }}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="body2" fontWeight="medium" color="primary" gutterBottom>
                        {t('tasks.weeklyTasks.card.taskHours')}
                      </Typography>
                      {getTaskDays(task).map((day) => (
                        <Box key={day.day} sx={{ mb: 1 }}>
                          <Typography variant="caption" fontWeight="bold">
                            {day.dayName}:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {day.repetitionTimes.map((time: string, index: number) => (
                              <TaskTimeChip
                                key={index}
                                label={time}
                                size="small"
                                status={getTaskTimeColor(task, time)}
                              />
                            ))}
                          </Box>
                        </Box>
                      ))}
                      <Chip 
                        label={t('tasks.weeklyTasks.card.toleranceSuffix', { minutes: task.startTolerance || 15 })}
                        size="small"
                        sx={{ 
                          mt: 1,
                          height: 20, 
                          fontSize: '0.7rem', 
                          bgcolor: 'primary.50', 
                          color: 'primary.main'
                        }}
                      />
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                    {task.personnelName ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {task.personnelName}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="error" fontStyle="italic">
                        {t('tasks.weeklyTasks.card.unassignedPersonnel')}
                      </Typography>
                    )}
                    
                    {task.completionType === 'qr' && !task.fromDatabase && statusFilter !== 'completed' && statusFilter !== 'missed' && (
                      <Button
                        size="small"
                        startIcon={<QrCodeIcon />}
                        variant="outlined"
                        color="primary"
                        onClick={(e) => onOpenQrPrintModal(task, e)}
                        sx={{ ml: 'auto', minWidth: 'auto', fontSize: '0.75rem', py: 0.5 }}
                      >
                        {t('tasks.weeklyTasks.card.printQR')}
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <>
          {statusFilter === 'completed' ? (
            // Tamamlanan görevler için özel tablo gösterimi
            <CompletedWeeklyTasksTable 
              tasks={filteredCompletedTasks}
              getStatusColor={getStatusColor}
              getStatusIcon={getStatusIcon}
              getStatusLabel={getStatusLabel}
              tableTitle={t('tasks.weeklyTasks.completedTable.title')}
              t={t}
              i18n={i18n}
              getWeekdayShort={getWeekdayShort}
            />
          ) : statusFilter === 'missed' ? (
            // Kaçırılan görevler için özel tablo gösterimi
            <CompletedWeeklyTasksTable 
              tasks={filteredMissedTasks}
              getStatusColor={getStatusColor}
              getStatusIcon={getStatusIcon}
              getStatusLabel={getStatusLabel}
              tableTitle={t('tasks.weeklyTasks.completedTable.missedTitle')}
              t={t}
              i18n={i18n}
              getWeekdayShort={getWeekdayShort}
            />
          ) : (
            // Normal görevler için standart tablo gösterimi
            <WeeklyTasksTable 
              tasks={filteredTasks}
              statusFilter={statusFilter}
              onShowTaskDetail={onShowTaskDetail}
              onOpenQrPrintModal={onOpenQrPrintModal}
              getStatusColor={getStatusColor}
              getStatusIcon={getStatusIcon}
              getStatusLabel={getStatusLabel}
              getTaskTimeColor={getTaskTimeColor}
              t={t}
              getTaskDays={getTaskDays}
            />
          )}
        </>
      )}
    </>
  );
};

export default WeeklyTasks; 